import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import useUsageDeleteConfirm from '../../hooks/useUsageDeleteConfirm';
import { filterFeaturesAvailableToLink } from '../../utils/filteringUtils';
import { useFilterSort, useModals, useDerivedData, useFamilySelection, useFeatureSelection, useActiveProject, useFamiliesData } from './hooks';
import {
  handleSelectFamily as baseHandleSelectFamily,
  handleEditFamily as baseHandleEditFamily,
  handleRenameFamily as baseHandleRenameFamily,
  handleCreateFamily as baseHandleCreateFamily,
  handleDeleteFamilyClick as baseHandleDeleteFamilyClick,
  handleDeleteFamilySubmit as baseHandleDeleteFamilySubmit,
  handleShareFamily as baseHandleShareFamily,
  handleUnlinkFamilyFromProject as baseHandleUnlinkFamilyFromProject,
  handleUnshareFamilyFromProject as baseHandleUnshareFamilyFromProject,
  handleLinkExistingFamilies as baseHandleLinkExistingFamilies,
  handleExportFamily as baseHandleExportFamily,
  handleImportClick as baseHandleImportClick,
  handleFileChange as baseHandleFileChange,
  handleSelectFeature as baseHandleSelectFeature,
  handleFeatureMappingSubmit as baseHandleFeatureMappingSubmit,
  toggleFeature as baseToggleFeature,
  toggleFolder as baseToggleFolder,
  handleAddAttrRow as baseHandleAddAttrRow,
  handleRemoveAttrRow as baseHandleRemoveAttrRow,
  handleAttrChange as baseHandleAttrChange,
  handleValueFieldChange as baseHandleValueFieldChange,
  handleValuePropertyChange as baseHandleValuePropertyChange,
  handleReorderValues as baseHandleReorderValues,
  handleSetDefaultValue as baseHandleSetDefaultValue,
  handleAddValueAttr as baseHandleAddValueAttr,
  handleRemoveValueAttr as baseHandleRemoveValueAttr,
  handleValueAttrChange as baseHandleValueAttrChange,
  handleViewChangeDetails as baseHandleViewChangeDetails,
  handleRevertMapping as baseHandleRevertMapping,
  handleDeleteFeature as baseHandleDeleteFeature,
  handleDeleteValue as baseHandleDeleteValue,
  handleAddValue as baseHandleAddValue,
  handleAddFeatureInline as baseHandleAddFeatureInline,
  handleSaveFeatureProperties as baseHandleSaveFeatureProperties,
  handleAddValueInline as baseHandleAddValueInline,
  handleRenameFeatureSubmit as baseHandleRenameFeatureSubmit,
  handleRenameValueSubmit as baseHandleRenameValueSubmit
} from './utils/handlers';
import FamiliesSelector from './components/FamiliesSelector';
import FamilyView from './components/FamilyView';
import { FamilyEditor } from './components/FamilyEditor';
import { FeatureMappingModal, CreateFamilyModal, DeleteFamilyModal, ChangeDetailsModal, DiffView } from './components/modals';
import { NoSelectionPlaceholder } from './components/shared';
import ShareFamilyModal from '../ProjectDashboard/components/ShareFamilyModal';
import LinkFamilyModal from '../ProjectDashboard/components/LinkFamilyModal';
import NoActiveProject from '../../components/NoActiveProject';

export default function FamiliesPage() {
    try {
        const navigate = useNavigate();
    const overlayMouseDown = useRef(false);
    const prevFamilyIdRef = useRef(null);
    const fileInputRef = useRef(null);

    // Use hook for active project/version state
    const {
        activeProjectId,
        activeProjectName,
        activeVersionId,
        isProduction,
        setActiveProjectId,
        setActiveProjectName,
        setActiveVersionId
    } = useActiveProject();

    // Use hook for raw data state
    const {
        families,
        projects,
        rulesets,
        features,
        loading,
        setFamilies,
        setProjects,
        setRulesets,
        setFeatures,
        setLoading,
        fetchData
    } = useFamiliesData();

    // Use hook for family selection state (includes auto-select logic)
    const {
        selectedFamilyId,
        setSelectedFamilyId,
        isEditMode,
        setIsEditMode,
        isCreatingFamily,
        setIsCreatingFamily,
        newFamilyName,
        setNewFamilyName,
        isEditingName,
        setIsEditingName,
        editFamilyName,
        setEditFamilyName,
        savingFamilyName,
        setSavingFamilyName
    } = useFamilySelection(families, projects, activeProjectId);

    // Use hook for filter/sort state
    const {
        searchQuery,
        setSearchQuery,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        activitySortBy,
        setActivitySortBy,
        activitySortOrder,
        setActivitySortOrder
    } = useFilterSort();

    // Use hook for feature selection state
    const {
        selectedFeatureId,
        setSelectedFeatureId,
        friendlyName,
        setFriendlyName,
        description,
        setDescription,
        inputType,
        setInputType,
        customAttrs,
        setCustomAttrs,
        featureValues,
        setFeatureValues,
        valueSchema,
        setValueSchema,
        cardLayout,
        setCardLayout,
        isSystem,
        setIsSystem,
        isOutput,
        setIsOutput,
        selectedValueId,
        setSelectedValueId,
        savingFeature,
        setSavingFeature,
        featureSearchQuery,
        setFeatureSearchQuery,
        expandedFolders,
        setExpandedFolders,
        expandedFeatures,
        setExpandedFeatures,
        isEditingFeatureName,
        setIsEditingFeatureName,
        editFeatureNameText,
        setEditFeatureNameText,
        isSavingFeatureName,
        setIsSavingFeatureName,
        isEditingValueName,
        setIsEditingValueName,
        editValueNameText,
        setEditValueNameText,
        isSavingValueName,
        setIsSavingValueName,
        inlineNewValTexts,
        setInlineNewValTexts,
        newFeatureInlineName,
        setNewFeatureInlineName,
        isAddingFeatureInline,
        setIsAddingFeatureInline,
        newValueText,
        setNewValueText,
        isAddingValue,
        setIsAddingValue
    } = useFeatureSelection();

    // Use hook for modal state
    const {
        isMappingFeatureModalOpen,
        setIsMappingFeatureModalOpen,
        mappingFeatureType,
        setMappingFeatureType,
        newFeatureName: modalNewFeatureName,
        setNewFeatureName: setModalNewFeatureName,
        selectedFeatureToLink,
        setSelectedFeatureToLink,
        mappingSaving,
        setMappingSaving,
        shareFamily,
        setShareFamily,
        isLinkFamilyOpen,
        setIsLinkFamilyOpen,
        isDeletingFamily,
        setIsDeletingFamily,
        deleteRuleSetsCheck,
        setDeleteRuleSetsCheck,
        deletingFamilyId,
        setDeletingFamilyId,
        activeLogDetails,
        setActiveLogDetails,
        loadingLogDiff,
        setLoadingLogDiff,
        logDiffEntry,
        setLogDiffEntry,
        logRulesetData,
        setLogRulesetData
    } = useModals();

    // Use hook for derived data
    const {
        familiesWithLastChange,
        selectedFamily,
        filteredFamilies,
        familyRulesets,
        familyFeatures,
        familyProjects,
        latestChanges,
        featuresTree
    } = useDerivedData(
        families,
        rulesets,
        features,
        projects,
        selectedFamilyId,
        activeProjectId,
        searchQuery,
        sortBy,
        sortOrder,
        activitySortBy,
        activitySortOrder,
        featureSearchQuery
    );

    // Initial data load - use fetchData from useFamiliesData hook
    useEffect(() => {
        fetchData();
    }, []);

    // Re-fetch when version changes
    useEffect(() => {
        const handleGlobalVersionChange = () => {
            const nextVersionId = localStorage.getItem('activeVersionId') || '00000000-0000-0000-0000-000000000000';
            setActiveVersionId(nextVersionId);
            fetchData();
        };
        window.addEventListener('activeVersionChanged', handleGlobalVersionChange);
        return () => window.removeEventListener('activeVersionChanged', handleGlobalVersionChange);
    }, [fetchData, setActiveVersionId]);

    // Update edit state name input whenever selected family changes
    useEffect(() => {
        if (selectedFamily) {
            setEditFamilyName(selectedFamily.name);
        }
        
        const idChanged = prevFamilyIdRef.current !== selectedFamilyId;
        prevFamilyIdRef.current = selectedFamilyId;

        if (idChanged) {
            setIsEditingName(false);
            setSelectedFeatureId(null);
            setSelectedValueId(null);
            setExpandedFolders({});
            setExpandedFeatures({});
        }
    }, [selectedFamily, selectedFamilyId]);

    // Create curried handlers from imported handler factories
    const handleSelectFamily = baseHandleSelectFamily(setSelectedFamilyId, setIsEditMode);
    const handleEditFamily = baseHandleEditFamily(setSelectedFamilyId, setIsEditMode);

    // Family CRUD handlers
    const handleExportFamily = baseHandleExportFamily;
    const handleShareFamily = baseHandleShareFamily(fetchData, setShareFamily);
    const handleUnlinkFamilyFromProject = baseHandleUnlinkFamilyFromProject(fetchData);
    const handleUnshareFamilyFromProject = baseHandleUnshareFamilyFromProject(fetchData);
    const handleLinkExistingFamilies = baseHandleLinkExistingFamilies(fetchData, setIsLinkFamilyOpen);
    const handleImportClick = baseHandleImportClick(fileInputRef);
    const handleFileChange = (e) => baseHandleFileChange(e, activeProjectId, setLoading, fetchData, setSelectedFamilyId);

    const handleCreateFamily = (e) => baseHandleCreateFamily(e, newFamilyName, activeProjectId, setNewFamilyName, setIsCreatingFamily, fetchData, setSelectedFamilyId);
    const handleRenameFamily = baseHandleRenameFamily(selectedFamilyId, editFamilyName, setFamilies, setIsEditingName, setSavingFamilyName, fetchData);
    const handleDeleteFamilyClick = baseHandleDeleteFamilyClick(setDeletingFamilyId, setIsDeletingFamily, setDeleteRuleSetsCheck);
    const handleDeleteFamilySubmit = baseHandleDeleteFamilySubmit(setSavingFamilyName, deletingFamilyId, families, deleteRuleSetsCheck, setIsDeletingFamily, setDeletingFamilyId, setDeleteRuleSetsCheck, fetchData, setSelectedFamilyId, setIsEditMode);

    // Feature handlers
    const { confirmDelete, modal: deleteUsageModal } = useUsageDeleteConfirm();
    const handleDeleteFeature = baseHandleDeleteFeature(selectedFeatureId, features, setSelectedFeatureId, setSavingFeature, fetchData, confirmDelete);
    // Selection handler for the Feature Config sub-pane - must be defined first as it's used by other handlers
    const handleSelectFeature = baseHandleSelectFeature(setSelectedFeatureId, setFriendlyName, setDescription, setInputType, setIsEditingFeatureName, setIsEditingValueName, setCustomAttrs, setFeatureValues, setSelectedValueId, setExpandedFeatures, setExpandedFolders, setIsEditMode, setValueSchema, setIsSystem, setCardLayout, setIsOutput);

    const handleAddValue = (e) => baseHandleAddValue(e, newValueText, selectedFeatureId, setNewValueText, setIsAddingValue, selectedFamilyId, setFeatures, setSelectedFeatureId, handleSelectFeature, fetchData);
    const handleAddFeatureInline = (e) => baseHandleAddFeatureInline(e, newFeatureInlineName, selectedFamilyId, setNewFeatureInlineName, setIsAddingFeatureInline, fetchData);
    const handleAddValueInline = baseHandleAddValueInline(inlineNewValTexts, selectedFamilyId, setInlineNewValTexts, setFeatures, handleSelectFeature, fetchData);
    const handleRenameFeatureSubmit = baseHandleRenameFeatureSubmit(selectedFeatureId, editFeatureNameText, selectedFamilyId, setIsSavingFeatureName, setFeatures, handleSelectFeature, setIsEditingFeatureName, selectedValueId);
    const handleRenameValueSubmit = baseHandleRenameValueSubmit(selectedFeatureId, selectedValueId, editValueNameText, selectedFamilyId, setIsSavingValueName, setFeatures, handleSelectFeature, setIsEditingValueName);

    // Attribute and value handlers
    const handleAddAttrRow = baseHandleAddAttrRow(setCustomAttrs);
    const handleRemoveAttrRow = baseHandleRemoveAttrRow(setCustomAttrs);
    const handleAttrChange = baseHandleAttrChange(setCustomAttrs);
    const handleValueFieldChange = baseHandleValueFieldChange(setFeatureValues);
    const handleValuePropertyChange = baseHandleValuePropertyChange(setFeatureValues);
    const handleReorderValues = baseHandleReorderValues(selectedFeatureId, featureValues, setFeatureValues, families, setFamilies);
    const handleSetDefaultValue = baseHandleSetDefaultValue(setFeatureValues);
    const handleAddValueAttr = baseHandleAddValueAttr(setFeatureValues);
    const handleRemoveValueAttr = baseHandleRemoveValueAttr(setFeatureValues);
    const handleValueAttrChange = baseHandleValueAttrChange(setFeatureValues);

    // Tree toggle handlers
    const toggleFeature = baseToggleFeature(expandedFeatures, setExpandedFeatures);
    const toggleFolder = baseToggleFolder(expandedFolders, setExpandedFolders);

    // Save Feature Properties
    const handleSaveFeatureProperties = baseHandleSaveFeatureProperties(selectedFeatureId, friendlyName, description, inputType, customAttrs, featureValues, selectedFamilyId, selectedValueId, setSavingFeature, setFeatures, handleSelectFeature, fetchData, valueSchema, isSystem, cardLayout, isOutput);

    // Handle feature mapping mapping modal submission
    const handleFeatureMappingSubmit = baseHandleFeatureMappingSubmit(selectedFamilyId, mappingFeatureType, modalNewFeatureName, selectedFeatureToLink, features, setModalNewFeatureName, setIsMappingFeatureModalOpen, setMappingSaving, fetchData, setSelectedFeatureId);

    // View change details handler
    const handleViewChangeDetails = baseHandleViewChangeDetails(activeVersionId, features, setActiveLogDetails, setLoadingLogDiff, setLogDiffEntry, setLogRulesetData);

    // Revert mapping handler
    const handleRevertMapping = baseHandleRevertMapping(selectedFamilyId, features, setSelectedFamilyId, fetchData);

    // Suggestions list for feature properties
    const suggestions = ['friendlyName', 'mappnc'];
    const activeFeature = features.find(v => v.id === selectedFeatureId);
    const selectedValueIndex = featureValues.findIndex(val => val.id === selectedValueId);
    const selectedValue = selectedValueIndex !== -1 ? featureValues[selectedValueIndex] : null;
    
    // Feature handlers that depend on selectedValue (must be defined after selectedValue)
    const handleDeleteValue = baseHandleDeleteValue(selectedFeatureId, selectedValueId, selectedValue, setSelectedValueId, fetchData, confirmDelete, setFeatureValues);

    // Filter features list for features selector in mapping modal (exclude ones already in this family)
    const featuresAvailableToLink = useMemo(() => {
        return filterFeaturesAvailableToLink(features, selectedFamilyId);
    }, [features, selectedFamilyId]);

    const activeProject = projects.find(p => String(p.id) === String(activeProjectId)) || { id: activeProjectId, linkedFamilies: [] };

    const handleSelectLinkedProject = (project) => {
        if (!project?.id) return;
        localStorage.setItem('activeProjectId', project.id);
        localStorage.setItem('activeProjectName', project.name || '');
        window.dispatchEvent(new Event('activeProjectChanged'));
    };

    const handleLinkToActive = async (family) => {
        if (!activeProjectId || !family?.id) return;
        await handleLinkExistingFamilies(activeProjectId, [family.id]);
    };

    if (!activeProjectId) {
        return (
            <NoActiveProject description="Families are managed inside the active project. Select a project in the header, or create one from the project menu." />
        );
    }

    return (
        <div style={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden', flexDirection: 'column' }} className="fade-in">
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Left Pane: Families Selector */}
            <FamiliesSelector
                loading={loading}
                families={families}
                projects={projects}
                filteredFamilies={filteredFamilies}
                selectedFamilyId={selectedFamilyId}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                isEditMode={isEditMode}
                fileInputRef={fileInputRef}
                onSelectFamily={handleSelectFamily}
                onEditFamily={handleEditFamily}
                onExportFamily={handleExportFamily}
                onShareFamily={setShareFamily}
                onDeleteFamily={handleDeleteFamilyClick}
                onCreateFamily={setIsCreatingFamily}
                onImportClick={handleImportClick}
                onFileChange={handleFileChange}
                onSearchChange={setSearchQuery}
                onSortByChange={setSortBy}
                onSortOrderToggle={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                onLinkExistingFamily={() => setIsLinkFamilyOpen(true)}
                onOpenSimulator={(familyId) => {
                    const explicitId = (typeof familyId === 'string' || typeof familyId === 'number') ? familyId : '';
                    const id = explicitId || selectedFamilyId;
                    if (id) navigate(`/simulator?familyId=${id}`);
                    else navigate('/simulator');
                }}
                activeProjectId={activeProjectId}
            />

            {/* Right Pane: Stats or Editor View */}
            <div style={{
                flex: 1,
                background: 'var(--bg-page)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden'
            }}>
                {selectedFamily ? (
                    !isEditMode ? (
                        /* Right Pane: STATS VIEW */
                        <FamilyView
                            selectedFamily={selectedFamily}
                            activeProjectName={activeProjectName}
                            familyRulesets={familyRulesets}
                            familyFeatures={familyFeatures}
                            familyProjects={familyProjects}
                            latestChanges={latestChanges}
                            activitySortBy={activitySortBy}
                            activitySortOrder={activitySortOrder}
                            onExportFamily={handleExportFamily}
                            onEditFamily={handleEditFamily}
                            onShareFamily={setShareFamily}
                            onDeleteFamily={handleDeleteFamilyClick}
                            onActivitySortByChange={setActivitySortBy}
                            onActivitySortOrderToggle={() => setActivitySortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                            onViewChangeDetails={handleViewChangeDetails}
                            onRevertMapping={handleRevertMapping}
                            onSelectProject={handleSelectLinkedProject}
                            activeProjectId={activeProjectId}
                            onUnshareFamily={handleUnshareFamilyFromProject}
                            onUnlinkFamily={handleUnlinkFamilyFromProject}
                            onLinkToActive={handleLinkToActive}
                            onSelectFeature={(feat) => {
                                setIsEditMode(true);
                                handleSelectFeature(feat);
                            }}
                            onSelectRuleset={(rs) => navigate(`/rules?familyId=${selectedFamily.id}&rulesetId=${rs.id}`)}
                        />
                    ) : (
                        /* Right Pane: FAMILY EDITOR VIEW */
                        <FamilyEditor
                            selectedFamily={selectedFamily}
                            featureSearchQuery={featureSearchQuery}
                            setFeatureSearchQuery={setFeatureSearchQuery}
                            expandedFolders={expandedFolders}
                            setExpandedFolders={setExpandedFolders}
                            expandedFeatures={expandedFeatures}
                            setExpandedFeatures={setExpandedFeatures}
                            selectedFeatureId={selectedFeatureId}
                            setSelectedFeatureId={setSelectedFeatureId}
                            selectedValueId={selectedValueId}
                            setSelectedValueId={setSelectedValueId}
                            isEditingName={isEditingName}
                            setIsEditingName={setIsEditingName}
                            editFamilyName={editFamilyName}
                            setEditFamilyName={setEditFamilyName}
                            savingFamilyName={savingFamilyName}
                            isEditingFeatureName={isEditingFeatureName}
                            setIsEditingFeatureName={setIsEditingFeatureName}
                            editFeatureNameText={editFeatureNameText}
                            setEditFeatureNameText={setEditFeatureNameText}
                            isSavingFeatureName={isSavingFeatureName}
                            isEditingValueName={isEditingValueName}
                            setIsEditingValueName={setIsEditingValueName}
                            editValueNameText={editValueNameText}
                            setEditValueNameText={setEditValueNameText}
                            isSavingValueName={isSavingValueName}
                            isAddingFeatureInline={isAddingFeatureInline}
                            newFeatureInlineName={newFeatureInlineName}
                            setNewFeatureInlineName={setNewFeatureInlineName}
                            inlineNewValTexts={inlineNewValTexts}
                            setInlineNewValTexts={setInlineNewValTexts}
                            friendlyName={friendlyName}
                            setFriendlyName={setFriendlyName}
                            description={description}
                            setDescription={setDescription}
                            inputType={inputType}
                            setInputType={setInputType}
                            isSystem={isSystem}
                            setIsSystem={setIsSystem}
                            isOutput={isOutput}
                            setIsOutput={setIsOutput}
                            valueSchema={valueSchema}
                            setValueSchema={setValueSchema}
                            cardLayout={cardLayout}
                            setCardLayout={setCardLayout}
                            customAttrs={customAttrs}
                            setCustomAttrs={setCustomAttrs}
                            featureValues={featureValues}
                            setFeatureValues={setFeatureValues}
                            savingFeature={savingFeature}
                            activeFeature={activeFeature}
                            selectedValue={selectedValue}
                            featuresTree={featuresTree}
                            features={familyFeatures}
                            suggestions={suggestions}
                            onRenameFamily={handleRenameFamily}
                            onSelectFeature={handleSelectFeature}
                            onToggleFolder={toggleFolder}
                            onToggleFeature={toggleFeature}
                            onRenameFeatureSubmit={handleRenameFeatureSubmit}
                            onRenameValueSubmit={handleRenameValueSubmit}
                            onSaveFeatureProperties={handleSaveFeatureProperties}
                            onAddFeatureInline={handleAddFeatureInline}
                            onAddValueInline={handleAddValueInline}
                            onAddAttrRow={handleAddAttrRow}
                            onRemoveAttrRow={handleRemoveAttrRow}
                            onAttrChange={handleAttrChange}
                            onValueFieldChange={handleValueFieldChange}
                            onValuePropertyChange={handleValuePropertyChange}
                            onReorderValues={handleReorderValues}
                            onCombinationsGenerated={() => fetchData({ selectFeatureId: selectedFeatureId, selectValueId: selectedValueId })}
                            onSetDefaultValue={handleSetDefaultValue}
                            onAddValueAttr={handleAddValueAttr}
                            onRemoveValueAttr={handleRemoveValueAttr}
                            onValueAttrChange={handleValueAttrChange}
                            onDeleteFeature={handleDeleteFeature}
                            onDeleteValue={handleDeleteValue}
                            onExitEditMode={() => setIsEditMode(false)}
                            onDeleteFamilyClick={(e) => handleDeleteFamilyClick(e, selectedFamily.id)}
                        />
                    )
                ) : (
                    /* Right Pane: NO SELECTION PLACEHOLDER */
                    <NoSelectionPlaceholder />
                )}
            </div>

            {deleteUsageModal}

            <ShareFamilyModal
                isOpen={!!shareFamily}
                onClose={() => setShareFamily(null)}
                family={shareFamily}
                projects={projects}
                currentProjectId={activeProjectId}
                onShare={handleShareFamily}
            />

            <LinkFamilyModal
                isOpen={isLinkFamilyOpen}
                onClose={() => setIsLinkFamilyOpen(false)}
                selectedProject={activeProject}
                onLink={(familyIds) => handleLinkExistingFamilies(activeProjectId, familyIds)}
            />

            {/* Modal: Delete Family */}
            <DeleteFamilyModal
                isOpen={isDeletingFamily}
                onClose={() => {
                    setIsDeletingFamily(false);
                    setDeletingFamilyId(null);
                    setDeleteRuleSetsCheck(false);
                }}
                onSubmit={handleDeleteFamilySubmit}
                deletingFamilyId={deletingFamilyId}
                families={families}
                deleteRuleSetsCheck={deleteRuleSetsCheck}
                setDeleteRuleSetsCheck={setDeleteRuleSetsCheck}
                saving={false}
            />

            {/* Modal: Create Family */}
            <CreateFamilyModal
                isOpen={isCreatingFamily}
                onClose={() => setIsCreatingFamily(false)}
                onSubmit={handleCreateFamily}
                newFamilyName={newFamilyName}
                setNewFamilyName={setNewFamilyName}
                saving={savingFamilyName}
                activeProjectId={activeProjectId}
            />

            {/* Modal: Link / Create Scoped Feature */}
            <FeatureMappingModal
                isOpen={isMappingFeatureModalOpen}
                onClose={() => setIsMappingFeatureModalOpen(false)}
                onSubmit={handleFeatureMappingSubmit}
                selectedFamily={selectedFamily}
                mappingFeatureType={mappingFeatureType}
                setMappingFeatureType={setMappingFeatureType}
                newFeatureName={modalNewFeatureName}
                setNewFeatureName={setModalNewFeatureName}
                selectedFeatureToLink={selectedFeatureToLink}
                setSelectedFeatureToLink={setSelectedFeatureToLink}
                featuresAvailableToLink={featuresAvailableToLink}
                mappingSaving={mappingSaving}
            />

            <ChangeDetailsModal
                isOpen={activeLogDetails !== null}
                onClose={() => setActiveLogDetails(null)}
                log={activeLogDetails}
                loading={loadingLogDiff}
                diffEntry={logDiffEntry}
                rulesetData={logRulesetData}
                features={features}
                families={families}
                onGoToEditor={(type, id) => {
                    if (type === 'RuleSet') {
                        navigate(`/rules?rulesetId=${id}`);
                    } else if (type === 'Feature') {
                        setIsEditMode(true);
                        setSelectedFeatureId(id);
                        setActiveLogDetails(null);
                    }
                }}
            />
            </div>
        </div>
    );
    } catch (error) {
        throw error;
    }
}


